/*
 * Gráfica de barras horizontales, réplica de Chart.js (indexAxis 'y') del HTML:
 * ejes con ticks en COP, gridlines, animación de entrada, hover y tooltip.
 */
import { useEffect, useRef, useState } from "react";

import Tooltipx from "./Tooltipx";
import { buildTicks, fmtCOP, htmlTheme } from "./utils/carthelp";

const AXIS_H = 22;

/**
 * Renderiza una gráfica de barras horizontales con ejes y gridlines
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Filas [{ label, value, color, raw }]
 * @param {Function} props.onItemClick Callback con la fila y su índice al hacer click
 * @param {number} props.height Alto total del área de la gráfica
 * @param {number} props.labelWidth Ancho de la columna de etiquetas
 */
const Barlistx = ({ data = [], onItemClick, height = 300, labelWidth = 170 }) => {
  const { ticks, top } = buildTicks(Math.max(1, ...data.map((mItem) => Math.abs(mItem.value))));
  const bClickable = typeof onItemClick === "function";
  const nRowH = (height - AXIS_H) / Math.max(1, data.length);
  const nBarH = Math.min(nRowH * 0.72, 26);

  const containerRef = useRef(null);
  const [bMounted, setMounted] = useState(false);
  const [nHover, setHover] = useState(null);
  const [mTip, setTip] = useState(null);

  //Dispara la animación de entrada de las barras
  useEffect(() => {
    const nTimer = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(nTimer);
  }, []);

  //Actualiza la posición del tooltip siguiendo el mouse
  const handleMove = (oEvent, mItem) => {
    const oRect = containerRef.current?.getBoundingClientRect();
    if (!oRect) return;
    setTip({
      x: oEvent.clientX - oRect.left,
      y: oEvent.clientY - oRect.top,
      title: mItem.raw || mItem.label,
      lines: [fmtCOP(mItem.value)],
    });
  };

  //Limpia el estado de hover y tooltip
  const handleLeave = () => {
    setHover(null);
    setTip(null);
  };

  return (
    <div ref={containerRef} style={{ display: "flex", height, position: "relative" }} onMouseLeave={handleLeave}>
      <Tooltipx tip={mTip} />
      <div style={{ width: labelWidth, flexShrink: 0 }}>
        {data.map((mItem, nIndex) => (
          <div
            key={nIndex}
            title={mItem.raw || mItem.label}
            style={{
              height: nRowH,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: 8,
              fontSize: 11,
              color: htmlTheme.muted,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {mItem.label}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, position: "relative" }}>
        {ticks.map((nTick) => (
          <div
            key={nTick}
            style={{
              position: "absolute",
              left: `${(nTick / top) * 100}%`,
              top: 0,
              bottom: AXIS_H,
              borderLeft: `1px solid ${nTick === 0 ? "#D5DAE8" : "#EDEFF7"}`,
            }}
          />
        ))}
        {data.map((mItem, nIndex) => (
          <div key={nIndex} style={{ height: nRowH, display: "flex", alignItems: "center" }}>
            <div
              onClick={bClickable ? () => onItemClick(mItem, nIndex) : undefined}
              onMouseEnter={() => setHover(nIndex)}
              onMouseMove={(oEvent) => handleMove(oEvent, mItem)}
              style={{
                width: bMounted ? `${(Math.abs(mItem.value) / top) * 100}%` : 0,
                height: nBarH,
                background: mItem.color || htmlTheme.navy,
                borderRadius: 5,
                minWidth: bMounted ? 2 : 0,
                cursor: bClickable ? "pointer" : "default",
                transition: "width .6s ease, filter .15s ease",
                filter: nHover === nIndex ? "brightness(1.15)" : "none",
              }}
            />
          </div>
        ))}
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: AXIS_H }}>
          {ticks.map((nTick) => (
            <span
              key={nTick}
              style={{
                position: "absolute",
                left: `${(nTick / top) * 100}%`,
                transform: "translateX(-50%)",
                fontSize: 10,
                color: htmlTheme.muted,
                whiteSpace: "nowrap",
              }}
            >
              {fmtCOP(nTick)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Barlistx;
